import { create } from 'zustand'
import { Product } from '../types'

interface ProductionItem {
  id: string
  productId: string
  productName: string
  startTime: number
  endTime: number
  quantity: number
}

interface ProductionState {
  products: Product[]
  activeProductions: ProductionItem[]
  isLoading: boolean
  error: string | null
  
  // Actions
  setProducts: (products: Product[]) => void
  addProduction: (production: ProductionItem) => void
  removeProduction: (productionId: string) => void
  loadProducts: () => Promise<void>
  startProduction: (productId: string) => Promise<void>
  collectProduction: (productionId: string) => Promise<void>
  reset: () => void
}

export const useProductionStore = create<ProductionState>((set) => ({
  products: [],
  activeProductions: [],
  isLoading: false,
  error: null,
  
  setProducts: (products) => set({ products, error: null }),
  
  addProduction: (production) => {
    set((state) => ({
      activeProductions: [...state.activeProductions, production]
    }))
  },
  
  removeProduction: (productionId: string) => {
    set((state) => ({
      activeProductions: state.activeProductions.filter(p => p.id !== productionId)
    }))
  },
  
  loadProducts: async () => {
    set({ isLoading: true, error: null })
    
    try {
      const response = await fetch('https://business-tycoon-worker.smnshzh.workers.dev/api/products')
      if (!response.ok) {
        throw new Error('Failed to load products')
      }
      
      const productsData: Product[] = await response.json()
      set({ products: productsData, isLoading: false })
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false 
      })
    }
  },
  
  startProduction: async (productId: string) => {
    try {
      const response = await fetch('https://business-tycoon-worker.smnshzh.workers.dev/api/production/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productId })
      })
      
      if (response.ok) {
        const productionData = await response.json()
        set((state) => ({
          activeProductions: [...state.activeProductions, productionData]
        }))
      }
    } catch (error) {
      console.error('Failed to start production:', error)
    }
  },
  
  collectProduction: async (productionId: string) => {
    try {
      const response = await fetch('https://business-tycoon-worker.smnshzh.workers.dev/api/production/collect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productionId })
      })
      
      if (response.ok) {
        set((state) => ({
          activeProductions: state.activeProductions.filter(p => p.id !== productionId)
        }))
      }
    } catch (error) {
      console.error('Failed to collect production:', error)
    }
  },
  
  reset: () => set({ products: [], activeProductions: [], error: null, isLoading: false })
}))
